import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ChevronDown, ChevronUp } from "lucide-react";
import { ModeType, TopLevelModes } from "@/lib/modes";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { ResetModeButton } from "@/components/common/reset-mode-button";
import { Spinner } from "@/components/ui/spinner";
import { renderTxt } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface GrammarConceptProps {
  explanation: string;
  mode: ModeType;
  restartMode: () => void;
}

export function ModeContextCard({
  explanation,
  mode,
  restartMode,
}: GrammarConceptProps) {
  const { t } = useTranslation(["common"]);

  // To make content scrollable with indicator
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const toggleExpand = () => setIsCollapsed(!isCollapsed);

  // Configure default header and description
  const [header, setHeader] = useState<string>("");
  const [description, setDescription] = useState<string>("");

  // Detect if expand button is needed
  useEffect(() => {
    // Check if content exceeds max height
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      // Show indicator if there's more content below
      setShowButton(
        scrollHeight > clientHeight &&
          scrollTop < scrollHeight - clientHeight - 10,
      );
      // setShowButton(contentHeight > maxHeight);
    }
  }, [explanation]);

  // Update header and description when explanation changes
  useEffect(() => {
    setHeader("common:context");

    switch (mode.topLevelMode) {
      case TopLevelModes.LangCoach:
        setDescription("pages:gameModes.langcoachDescription");
        break;
      case TopLevelModes.DailyLife:
        setDescription("pages:gameModes.dailylifeDescription");
        break;
      case TopLevelModes.ArgumentBattle:
        setDescription("pages:gameModes.argbattleDescription");
        break;
      case TopLevelModes.ImproTest:
        setDescription("pages:gameModes.improtestDescription");
        break;
      case TopLevelModes.Explainer:
        setDescription("pages:gameModes.explainerDescription");
        break;
      default:
        setDescription("pages:gameModes.unknownDescription");
        break;
    }
  }, [mode, t]);

  const restartModeWrapper = () => {
    setIsCollapsed(false);
    setShowButton(false);
    restartMode();
  };

  return (
    <Card
      className="p-4 w-full bg-cyan-50 text-black dark:bg-cyan-900 dark:text-gray-200"
      data-tour="mode-context"
    >
      <ResetModeButton
        className="-top-2 -right-2"
        restartMode={restartModeWrapper}
      />
      <CardHeader>
        <div className="flex flex-col mb-2">
          <CardTitle className="md:font-bold md:text-lg text-cyan-800 dark:text-cyan-500">
            {t(header)}
          </CardTitle>
          <CardDescription className="text-sm text-wrap">
            {t(description)}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className={`relative ${isCollapsed ? "pb-0" : "pb-4"}`}>
        <div
          ref={contentRef}
          className={`${isCollapsed ? "overflow-hidden" : "min-h-12 overflow-y-auto"} transition-all duration-300 ${
            isCollapsed
              ? showButton
                ? "max-h-8 md:max-h-28"
                : "max-h-28"
              : "max-h-60 md:max-h-80"
          }`}
        >
          {mode.hasInitialContext && explanation === "" ? (
            <div className="flex items-center justify-center gap-6">
              <Spinner className="size-8 text-cyan-800" />
            </div>
          ) : (
            explanation !== "" && (
              <div className="rounded-md text-sm md:text-base">
                <p dangerouslySetInnerHTML={renderTxt(explanation)} />
              </div>
            )
          )}
        </div>
        {/* Gradient fade */}
        {showButton && isCollapsed && (
          <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-cyan-50 dark:from-cyan-900 to-transparent pointer-events-none"></div>
        )}
      </CardContent>
      {showButton && (
        <div className="relative">
          <Button
            onClick={toggleExpand}
            className="absolute w-12 h-8 rounded-full p-2 -top-2 left-1/2 ml-auto bg-cyan-600 hover:bg-cyan-700"
            aria-label="Expand context"
          >
            {isCollapsed ? (
              <ChevronDown className={`h-5 w-5 text-white`} />
            ) : (
              <ChevronUp className={`h-5 w-5 text-white`} />
            )}
          </Button>
        </div>
      )}
    </Card>
  );
}
